/**
 * @file formatCategoryDisplayName.ts
 * @description Pure helper that maps raw extraction category keys to
 *   human-readable display strings. Handles BOTH snake_case
 *   (`fixed_terms`) and camelCase (`fixedTerms`) shapes because the
 *   backend has historically emitted both depending on which service
 *   produced the extraction. Output is always Title Case with words
 *   space-separated (`Fixed Terms`, `Variable Bonuses`, `Clauses`,
 *   `Other`) so the `CollapsibleTierHeader`'s `uppercase` CSS class
 *   renders readable glyphs ("FIXED TERMS") rather than the
 *   single-run "FIXEDTERMS" we were shipping before SE-4333 item E.
 *
 *   Lives in `features/contracts/utils` rather than next to the View
 *   because both the View AND the Container need it — Container uses
 *   the formatted name only for display, Container keys its
 *   collapsed-section state by the stable raw `key` so it cannot flap
 *   when this formatter evolves (e.g. i18n). Keeping the formatter
 *   here also means the Player Financials tab and the drawer stay
 *   byte-identical on category labels.
 * @module features/contracts/utils/formatCategoryDisplayName
 */

/**
 * Maps a raw category key to its display string.
 *
 * Normalisation rules:
 * 1. Insert a space at every lower→upper camelCase boundary
 *    (`fixedTerms` → `fixed Terms`).
 * 2. Replace underscores with spaces (`variable_bonuses` →
 *    `variable bonuses`).
 * 3. Collapse repeated whitespace and trim.
 * 4. Title-case each word (capitalize first letter, lowercase the
 *    rest) so the output is stable regardless of input casing.
 *
 * Examples:
 * - `fixed_terms` → `Fixed Terms`
 * - `fixedTerms`  → `Fixed Terms`
 * - `FIXED_TERMS` → `Fixed Terms`
 * - `variable_bonuses` / `variableBonuses` → `Variable Bonuses`
 * - `clauses` → `Clauses`
 * - `other`   → `Other`
 *
 * Returns the original string if it is empty so callers can defer
 * non-empty validation to their own guards.
 * @param key - Raw category key from the extractedData payload
 * @returns Display name suitable for the tier header + count badge
 */
export function formatCategoryDisplayName(key: string): string {
  if (key.length === 0) return key;
  const words = key
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(word => word.length > 0);
  return words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
